import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { authApiV2 } from '@/api/endpoints/auth';
import { AuthMarketingShell } from '@/features/auth/components/AuthMarketingShell';
import { AuthTextField } from '@/features/auth/components/AuthTextField';
import { PreLogGuestRedirect } from '@/features/auth/components/PreLogGuestRedirect';
import { isDevAuth } from '@/lib/dev-auth';
import { useAuthStore } from '@/stores/auth.store';

const forgotSchema = z.object({
  email: z.email('Please enter a valid email address'),
});

type ForgotFormData = z.infer<typeof forgotSchema>;

/** Rewrite server-built reset URL to the browser's current origin in local dev. */
function devResetUrlForCurrentOrigin(url: string): string {
  try {
    const remote = new URL(url);
    return `${window.location.origin}${remote.pathname}${remote.search}`;
  } catch {
    return url;
  }
}

function BackToSignInLink() {
  return (
    <Link
      to="/login"
      className="font-medium text-primary hover:text-primary hover:underline"
    >
      Back to sign in
    </Link>
  );
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [submitted, setSubmitted] = useState(false);
  const [inviteExpired, setInviteExpired] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [devResetUrl, setDevResetUrl] = useState<string | null>(null);
  const [devTemporaryPassword, setDevTemporaryPassword] = useState<string | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ForgotFormData>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: '' },
  });
  const emailValue = watch('email') ?? '';

  async function onSubmit(data: ForgotFormData) {
    setIsLoading(true);
    setError(null);
    try {
      const result = await authApiV2.forgotPassword({ email: data.email });
      // DEF-251 — pending invites complete registration instead of Password Reminder.
      if (result.next === 'register') {
        navigate(
          `/register?email=${encodeURIComponent(result.email)}`,
          { replace: true },
        );
        return;
      }
      if (result.next === 'claim') {
        navigate(
          `/login?email=${encodeURIComponent(result.email)}&claim=1`,
          { replace: true },
        );
        return;
      }
      // DEF-287 — an expired invite must never look like a normal password
      // reset; a working temp password would bypass the expired invitation.
      if (result.next === 'invite_expired') {
        setSubmittedEmail(data.email);
        setInviteExpired(true);
        return;
      }
      setSubmittedEmail(data.email);
      setDevResetUrl(
        result.devResetUrl
          ? devResetUrlForCurrentOrigin(result.devResetUrl)
          : null,
      );
      setDevTemporaryPassword(result.devTemporaryPassword ?? null);
      setSubmitted(true);
    } catch {
      setError('Unable to process your request. Please try again or contact support.');
    } finally {
      setIsLoading(false);
    }
  }

  if (isAuthenticated && !isDevAuth()) {
    return <PreLogGuestRedirect />;
  }

  if (inviteExpired) {
    return (
      <AuthMarketingShell
        title="Invitation expired"
        subtitle="Your invitation has expired. Please contact your administrator to extend your invitation."
        footer={<BackToSignInLink />}
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-warning-bg">
            <AlertTriangle className="h-6 w-6 text-warning" />
          </div>
          <p className="text-xs text-muted-foreground">
            No email was sent — a reset link won&apos;t restore access to an
            invitation that&apos;s already closed.
          </p>
          <button
            type="button"
            onClick={() => {
              setInviteExpired(false);
              setSubmittedEmail('');
            }}
            className="mt-6 w-full rounded-md border border-input px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            Try a different email
          </button>
        </div>
      </AuthMarketingShell>
    );
  }

  if (submitted) {
    return (
      <AuthMarketingShell
        title="Check your email"
        subtitle={`If an account exists for ${submittedEmail}, we've sent password reset instructions.`}
        footer={<BackToSignInLink />}
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
            <CheckCircle className="h-6 w-6 text-primary" />
          </div>
          <p className="text-xs text-muted-foreground">
            Didn&apos;t receive the email? Check your spam folder or try again.
          </p>
          {import.meta.env.DEV && (devTemporaryPassword || devResetUrl) && (
            <div
              className="mt-4 rounded-md border border-warning/30 bg-warning-bg px-3 py-2 text-left text-xs text-warning"
              data-clarity-mask="true"
            >
              <p className="font-medium">Local dev: email delivery is stubbed.</p>
              {devTemporaryPassword && (
                <p className="mt-1">
                  Sign in with this temporary password:{' '}
                  <span className="font-mono font-semibold">{devTemporaryPassword}</span>
                  . You should then see Update Password.
                </p>
              )}
              {devResetUrl && (
                <p className="mt-1 text-warning/80">
                  Reset-link shortcut:{' '}
                  <a
                    href={devResetUrl}
                    className="break-all font-mono font-semibold text-warning underline"
                  >
                    {devResetUrl}
                  </a>
                </p>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              setSubmitted(false);
              setSubmittedEmail('');
              setDevResetUrl(null);
              setDevTemporaryPassword(null);
            }}
            className="mt-6 w-full rounded-md border border-input px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            Try a different email
          </button>
        </div>
      </AuthMarketingShell>
    );
  }

  return (
    <AuthMarketingShell
      title="Forgot your password?"
      subtitle="Enter your email and we'll send you reset instructions."
      footer={<BackToSignInLink />}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
        className="flex flex-col gap-5"
      >
        {error && (
          <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="forgot-email" className="mb-1.5 block text-sm font-medium text-foreground">
            Email address
          </label>
          <AuthTextField
            {...register('email')}
            type="email"
            id="forgot-email"
            autoComplete="off"
            autoFocus
            value={emailValue}
            onClear={() => setValue('email', '', { shouldDirty: true, shouldValidate: true })}
            clearLabel="Clear email"
            isInvalid={!!errors.email}
            placeholder="you@company.com"
          />
          {errors.email && (
            <p className="mt-1 text-xs text-destructive-text">{errors.email.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {isLoading ? 'Sending…' : 'Send reset instructions'}
        </button>
      </form>
    </AuthMarketingShell>
  );
}
